import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { db } from '@/db/db';
import { PLANS, ROLES, SUPER_ROLES, BUSINESS_TYPES } from '@/config/constants';
import { useP2PStore } from '@/store/p2pStore';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// -------------------------------------------------------------
// BENEFICIO BETA: créditos ÚNICOS (una sola vez) para correos @beta.
// Se acredita 325 créditos SOLO la primera vez. NO se acumula cada mes:
// si quiere más créditos, debe recargar. src/store/authStore.ts
// -------------------------------------------------------------
const BETA_DOMAIN = '@beta';       // cualquier correo que termine en @beta (o @beta.com)
const STUDIO_TRIAL_DAYS = 14;

const isBetaEmail = (email: string): boolean =>
  email.trim().toLowerCase().endsWith(BETA_DOMAIN) || email.trim().toLowerCase().endsWith('@beta.com');

/**
 * El bono Beta se acredita en el SERVIDOR (RPC claim_beta_bonus), que valida
 * el dominio del correo y la idempotencia por usuario. El cliente ya no suma
 * saldo. Si no hay sesión cloud, no se acredita.
 */
const applyBetaCoins = async (email: string): Promise<void> => {
  if (!isBetaEmail(email)) return;
  try {
    const { claimBetaBonus } = await import('@/lib/wallet');
    const result = await claimBetaBonus();
    if (result.ok) {
      console.log(`🎁 Beta: bono acreditado por el servidor a ${email}`);
    }
  } catch (e) {
    console.warn('No se pudo acreditar beneficio beta:', e);
  }
};

/** Función PÚBLICA para acreditar el beneficio beta mensual (usada en el arranque). */
export const creditBetaMonthly = async (email?: string): Promise<void> => {
  if (!email) return;
  await applyBetaCoins(email);
};

// -------------------------------------------------------------
// TIPOS Y DEFINICIONES
// -------------------------------------------------------------
export type UserRole = keyof typeof ROLES | 'SUPPLIER';
export type BusinessType = keyof typeof BUSINESS_TYPES;
export type PlanType = keyof typeof PLANS;

export type LoginResult =
  | { status: 'SUCCESS'; user: User }
  | { status: 'WARNING_FREE_NEW_DEVICE'; user: User; message: string }
  | { status: 'ERROR'; message: string };

// Removed SUPER_ROLES definition, imported from constants

export interface User {
  id: string;
  full_name: string; // Cambiado de name
  email?: string | undefined;
  role: UserRole;
  business_id: string;
  business_type?: BusinessType | undefined;
  plan_type: PlanType;
  pin?: string | undefined;
  avatar_url?: string | undefined;
  is_active: boolean;
  supplier_id?: string; // Nuevo: Para proveedores
  branch_id?: string | undefined; // Multi-sede v109
  permissions?: string[] | undefined;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLocked: boolean;
  isLoading: boolean;

  // Acciones Locales
  login: (user: User) => void;
  setSession: (user: User) => void;
  logout: () => Promise<void>;
  lockSession: () => void;
  unlockSession: () => void;
  updateUser: (updates: Partial<User>) => void;

  // NUEVO: Refrescar sesión desde DB local
  refreshSession: () => Promise<void>;

  // Acciones Nube
  registerCloud: (data: {
    email: string;
    password: string;
    fullName: string;
    businessName: string;
    businessType: BusinessType;
    pin?: string;
    // 📍 Ubicación del negocio (obligatoria): la necesitan proveedores, envíos y subastas.
    country?: string;
    city?: string;
    address?: string;
  }) => Promise<{ success: boolean; error?: string; code?: string; user?: User }>;

  loginCloud: (email: string, password: string) => Promise<LoginResult>;

  // Recuperar contraseña (envía email de reseteo vía Supabase)
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;

  // Eliminar la cuenta del negocio (borra auth + datos locales)
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;

  // Helpers
  hasPermission: (allowedRoles: string | string[]) => boolean;
  can: (action: string) => boolean;
  getInitials: () => string;
  getRoleName: () => string;

  // NUEVO: Registro Local Offline
  registerLocal: (data: {
    fullName: string;
    businessName: string;
    businessType: BusinessType;
    pin: string;
    country?: string;
    city?: string;
    address?: string;
  }) => Promise<{ success: boolean; error?: string; user?: User }>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLocked: false,
      isLoading: false,

      // --- ACCIONES LOCALES ---
      login: (user) => {
        set({ user, isAuthenticated: true, isLocked: false, isLoading: false });
        // Registrar sesión del empleado (equipo + hora) para el panel de Equipos
        import('@/lib/devices').then(({ recordEmployeeSession }) => {
          const deviceName = useP2PStore.getState().deviceName;
          void recordEmployeeSession(user, deviceName);
        });
      },

      setSession: (user) => {
        set({ user, isAuthenticated: true, isLocked: false, isLoading: false });
      },

      // NUEVO: Sincroniza el estado de memoria con la DB real
      refreshSession: async () => {
        const { user } = get();
        if (!user || !user.id) return;

        try {
          // Buscamos el perfil más reciente en Dexie
          const dbUser = await db.profiles.get(user.id);
          if (dbUser) {
            const settings = await db.settings.where('business_id').equals(dbUser.business_id).first();

            const freshUser: User = {
              id: dbUser.id,
              full_name: dbUser.full_name,
              email: dbUser.email || undefined,
              role: dbUser.role as UserRole,
              business_id: dbUser.business_id,
              business_type: (settings?.business_type || user.business_type) as BusinessType,
              plan_type: (settings?.plan_type as PlanType) || user.plan_type,
              is_active: dbUser.is_active
            };

            // Actualizamos silenciosamente
            set({ user: freshUser });
          }
        } catch (e) {
          console.warn("No se pudo refrescar la sesión local", e);
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          // Cerrar la sesión abierta del empleado (hora de salida)
          const current = get().user;
          if (current?.id) {
            import('@/lib/devices').then(({ endEmployeeSession }) => void endEmployeeSession(current.id));
          }
          // if (supabase) await supabase.auth.signOut(); // Eliminado
          localStorage.removeItem('niiz-cart-storage');
          localStorage.removeItem('niiz-pos-storage');

          // 🔀 AISLAMIENTO MULTI-CUENTA: al cerrar sesión se OFRECE limpiar los
          // datos locales de ESTE negocio para que otra cuenta en el mismo
          // navegador no herede nada. Si el usuario prefiere conservarlos,
          // quedan aislados por `business_id` (nunca se mezclan).
          const businessId = String(current?.business_id || '').trim();
          if (businessId && businessId !== 'local') {
            try {
              const { purgeBusinessData, setStoredBusinessId, setPurgeOnLogout } = await import('@/lib/businessIsolation');
              const wantsPurge = window.confirm(
                '¿Borrar los datos locales de este negocio en este equipo?\n\n' +
                '· ACEPTAR → se limpian productos, ventas, caja y configuración de ESTE negocio (útil si otra persona usará el equipo).\n' +
                '· CANCELAR → se conservan y quedan aislados por negocio (se recuperan al volver a entrar).',
              );
              setPurgeOnLogout(wantsPurge);
              if (wantsPurge) {
                const deleted = await purgeBusinessData(businessId);
                console.log(`🧹 [AISLAMIENTO] Datos locales de "${businessId}" eliminados al cerrar sesión (${deleted} fila/s).`);
              } else {
                console.log(`🔒 [AISLAMIENTO] Datos de "${businessId}" conservados y aislados para la próxima sesión.`);
              }
              // Se recuerda el negocio para detectar el cambio en el próximo login.
              setStoredBusinessId(businessId);
            } catch (isoError) {
              console.warn('No se pudo aplicar el aislamiento al cerrar sesión:', isoError);
            }
          }
        } catch (e) {
          console.warn('Error logout local', e);
        } finally {
          set({ user: null, isAuthenticated: false, isLocked: false, isLoading: false });
        }
      },

      lockSession: () => set({ isLocked: true }),
      unlockSession: () => set({ isLocked: false }),

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null
        }));
      },

      // --- ACCIONES NUBE (REALES con Supabase) ---
      registerCloud: async ({ email, password, fullName, businessName, businessType, pin, country, city, address }) => {
        if (!isSupabaseConfigured() || !supabase) {
          return { success: false, error: "Supabase no configurado. Agrega VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en .env", code: 'NOT_CONFIGURED' };
        }

        try {
          // 1. Crear usuario en Supabase Auth (email + contraseña)
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: email.trim(),
            password,
            options: { data: { full_name: fullName.trim(), business_name: businessName.trim() } }
          });

          if (authError) {
            if (authError.message?.toLowerCase().includes('already registered') || authError.message?.toLowerCase().includes('already')) {
              return { success: false, error: 'Ese correo ya está registrado. Inicia sesión con correo y contraseña.', code: 'email-already-in-use' };
            }
            if (authError.message?.toLowerCase().includes('password')) {
              return { success: false, error: 'La contraseña debe tener al menos 6 caracteres.', code: 'weak-password' };
            }
            return { success: false, error: authError.message || 'Error al crear la cuenta', code: authError.code };
          }

          const uid = authData.user?.id;
          if (!uid) {
            return { success: false, error: 'No se pudo crear la cuenta. Revisa si la confirmación de email está desactivada (Supabase → Authentication → Providers → Email → Confirm email OFF).', code: 'email-confirmation' };
          }

          // 2. IDs del negocio y del perfil
          const businessId = `biz-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const now = new Date().toISOString();

          // 3. Guardar el perfil del usuario en la tabla `profiles` de Supabase
          //    (la sesión está activa por signUp con confirmación desactivada, así RLS lo permite)
          const { error: profileError } = await supabase.from('profiles').upsert({
            id: uid,
            business_id: businessId,
            full_name: fullName.trim(),
            email: email.trim(),
            role: 'ADMIN',
            pin: pin || null,
            is_active: true,
            created_at: now,
            updated_at: now,
          } as any);
          if (profileError) {
            console.error('Error guardando perfil en Supabase:', profileError);
            return { success: false, error: 'No se pudo guardar tu perfil en la nube: ' + profileError.message, code: 'PROFILE_SAVE_FAIL' };
          }

          // 4. Guardar la configuración del negocio en `settings` de Supabase
          //    🛠️ Se usa el GUARDADO SEGURO (upsert con auto-reparación de
          //    columnas y fallback select→update/insert). Una llamada directa a
          //    `.upsert({...}, { onConflict: 'business_id' })` producía:
          //    POST /settings?on_conflict=business_id 400 (Bad Request).
          const { saveRowSafely } = await import('@/lib/cloudSchemaRepair');
          const settingsPayload = {
            business_id: businessId,
            business_name: businessName.trim(),
            business_type: businessType,
            currency_symbol: '$',
            tax_rate: 0,
            // 📍 Ubicación obligatoria del negocio (envíos, proveedores, subastas).
            country: country?.trim() || null,
            city: city?.trim() || null,
            address: address?.trim() || null,
            plan_type: 'FREE',
            ai_enabled: true,
            marketplace_enabled: false,
            cloud_storage_active: false,
            // Campos que la tienda pública necesita desde el primer día.
            store_enabled: false,
            store_slug: 'tienda',
            created_at: now,
            updated_at: now,
          };
          const saved = await saveRowSafely('settings', settingsPayload, {
            onConflict: 'business_id',
            keyColumn: 'business_id',
          });
          if (!saved.ok) {
            console.error('Error guardando settings en Supabase:', saved.error);
            return {
              success: false,
              error: 'No se pudo guardar la configuración en la nube: ' + ((saved.error as any)?.message || 'error'),
              code: 'SETTINGS_SAVE_FAIL',
            };
          }
          console.log(`✅ [REGISTRO] Settings guardado en la nube (${saved.method}) · business_id=${businessId}`);

          // 5. Crear también perfil + configuración en LOCAL (Dexie) para operar
          try {
            await db.profiles.add({
              id: uid,
              business_id: businessId,
              full_name: fullName.trim(),
              email: email.trim(),
              role: 'ADMIN',
              pin: pin || '',
              is_active: true,
              created_at: now,
            } as any);
          } catch (e) { /* ya existe */ }

          try {
            const firmTrial = businessType === 'ACCOUNTING_FIRM';
            await db.settings.add({
              id: `settings-${businessId}`,
              business_id: businessId,
              business_name: businessName.trim(),
              business_type: businessType,
              currency_symbol: '$',
              tax_rate: 0,
              // 📍 Ubicación del negocio (local).
              country: country?.trim() || '',
              city: city?.trim() || '',
              address: address?.trim() || '',
              plan_type: firmTrial ? 'STUDIO_FIRM' : 'FREE',
              subscription_status: 'TRIAL',
              // Zanahoria Studio: prueba de 14 días. Al vencer, el cron baja a FREE.
              plan_started_at: now,
              auto_renew: false,
              ...(firmTrial
                ? {
                    subscription_expiry: new Date(Date.now() + STUDIO_TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
                    plan_expires_at: new Date(Date.now() + STUDIO_TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
                  }
                : {}),
              beta_type: 'NONE',
              ai_enabled: true,
              marketplace_enabled: false,
              created_at: now,
            } as any);
          } catch (e) { /* ya existe */ }

          // Trial de Studio en el SERVIDOR (14 días, una sola vez).
          if (businessType === 'ACCOUNTING_FIRM' && supabase) {
            try {
              await (supabase as any).rpc('start_studio_trial');
            } catch (e) {
              console.warn('No se pudo iniciar el trial de Studio en el servidor:', e);
            }
          }

          const newUser: User = {
            id: uid,
            full_name: fullName.trim(),
            email: email.trim(),
            role: 'ADMIN',
            business_id: businessId,
            business_type: businessType,
            plan_type: businessType === 'ACCOUNTING_FIRM' ? 'STUDIO_FIRM' : 'FREE',
            is_active: true,
            ...(pin ? { pin } : {}),
          };

          // Beneficio beta: monedas gratis si el correo es @beta
          await applyBetaCoins(email.trim());

          return { success: true, user: newUser };
        } catch (e: any) {
          console.error('registerCloud (Supabase):', e);
          return { success: false, error: e?.message || 'Error al registrar en la nube', code: e?.code };
        }
      },

      loginCloud: async (email, password) => {
        if (!isSupabaseConfigured() || !supabase) {
          return { status: 'ERROR', message: "Supabase no configurado. Usa tu PIN local." };
        }

        try {
          // 1. Autenticar con Supabase
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          });

          if (authError) {
            return { status: 'ERROR', message: 'Correo o contraseña incorrectos.' };
          }

          const userRecord = authData.user;
          const uid = userRecord.id;
          const metadata = (userRecord.user_metadata || {}) as any;

          // 2. Buscar el perfil en Supabase (tabla profiles)
          let businessId = '';
          let fullName = metadata.full_name || '';
          let businessType: BusinessType = 'RETAIL';
          let planType: PlanType = 'FREE';
          let subscriptionStatus = 'ACTIVE';
          let planExpiresAt: string | undefined;
          let cloudSettings: Record<string, unknown> | null = null;

          try {
            const { data: profileRow } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', uid)
              .single();
            if (profileRow) {
              businessId = (profileRow as any).business_id || '';
              fullName = (profileRow as any).full_name || fullName;
            }
          } catch (e) { /* sin perfil aún */ }

          if (!businessId) {
            businessId = `biz-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          }

          // 3. Buscar settings del negocio para conocer el tipo
          try {
            const { data: settingsRow } = await supabase
              .from('settings')
              .select('*')
              .eq('business_id', businessId)
              .limit(1)
              .single();
            if (settingsRow) {
              cloudSettings = settingsRow as Record<string, unknown>;
              businessType = ((settingsRow as any).business_type as BusinessType) || 'RETAIL';
              planType = ((settingsRow as any).plan_type as PlanType) || 'FREE';
              subscriptionStatus = String((settingsRow as any).subscription_status || 'ACTIVE');
              if (typeof (settingsRow as any).plan_expires_at === 'string') {
                planExpiresAt = (settingsRow as any).plan_expires_at;
              }
              fullName = fullName || ((settingsRow as any).business_name) || fullName;
            }
          } catch (e) { /* sin settings */ }

          // 4. Construir usuario de sesión
          const user: User = {
            id: uid,
            full_name: fullName || email.trim(),
            email: email.trim(),
            role: 'ADMIN',
            business_id: businessId,
            business_type: businessType,
            plan_type: planType,
            is_active: true,
          };

          // 5. Asegurar perfil y settings locales alineados con el negocio cloud.
          const existingProfile = await db.profiles.get(uid);
          if (!existingProfile) {
            await db.profiles.add({
              id: uid,
              business_id: businessId,
              full_name: fullName || email.trim(),
              email: email.trim(),
              role: 'ADMIN',
              pin: '',
              is_active: true,
              created_at: new Date().toISOString(),
            } as any);
          } else if (existingProfile.business_id !== businessId) {
            await db.profiles.update(uid, { business_id: businessId } as any);
          }

          // La configuración local puede tener un business_id viejo. Se
          // reconcilia con el de Supabase y se fuerza el plan descargado.
          let settingsRow = await db.settings.where('business_id').equals(businessId).first();
          if (!settingsRow) {
            const anySettings = await db.settings.toCollection().first();
            if (anySettings?.id) {
              await db.settings.update(anySettings.id, { business_id: businessId } as any);
              settingsRow = { ...anySettings, business_id: businessId };
            }
          }
          if (settingsRow?.id) {
            await db.settings.update(settingsRow.id, {
              plan_type: planType,
              subscription_status: subscriptionStatus,
              ...(planExpiresAt ? { plan_expires_at: planExpiresAt } : {}),
            } as any);
          } else {
            await db.settings.add({
              id: `settings-${businessId}`,
              business_id: businessId,
              business_name: String(cloudSettings?.['business_name'] || fullName || email.trim()),
              business_type: businessType,
              currency_symbol: '$',
              tax_rate: 0,
              plan_type: planType,
              subscription_status: subscriptionStatus,
              ...(planExpiresAt ? { plan_expires_at: planExpiresAt } : {}),
              beta_type: 'NONE',
              ai_enabled: true,
              marketplace_enabled: false,
              created_at: new Date().toISOString(),
            } as any);
          }

          // Beneficio beta: monedas gratis si el correo es @beta
          await applyBetaCoins(email.trim());

          return { status: 'SUCCESS', user };
        } catch (e: any) {
          console.error('loginCloud (Supabase):', e);
          return { status: 'ERROR', message: e?.message || 'Error al iniciar sesión en la nube' };
        }
      },

      // ─── RECUPERAR CONTRASEÑA (Supabase) ───
      resetPassword: async (email) => {
        if (!isSupabaseConfigured() || !supabase) {
          return { success: false, error: 'Supabase no configurado' };
        }
        try {
          // 🔑 El enlace del correo debe volver a la PÁGINA DE NUEVA CONTRASEÑA.
          const redirectTo = `${window.location.origin}/reset-password`;
          const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
          if (error) {
            return { success: false, error: error.message || 'No se pudo enviar el correo de recuperación' };
          }
          return { success: true };
        } catch (e: any) {
          return { success: false, error: e?.message || 'Error al recuperar contraseña' };
        }
      },

      // ─── ELIMINAR CUENTA (borra auth + datos) ───
      deleteAccount: async () => {
        const { user } = get();
        try {
          // 1. Borrar el usuario de Supabase Auth (si hay sesión)
          if (supabase) {
            try { await supabase.auth.admin?.deleteUser(user?.id || ''); } catch {}
            // Intentar el método normal de autoservicio
            const { data } = await supabase.auth.getSession();
            const accessToken = data.session?.access_token;
            if (accessToken && user?.id) {
              await fetch(`${import.meta.env['VITE_SUPABASE_URL']}/auth/v1/admin/users/${user.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}`, apikey: import.meta.env['VITE_SUPABASE_ANON_KEY'] || '' },
              }).catch(() => {});
            }
          }

          // 2. Borrar datos locales (perfil, settings, y datos del negocio)
          if (user?.business_id) {
            await db.settings.where('business_id').equals(user.business_id).delete();
            await db.products.where('business_id').equals(user.business_id).delete();
            await db.sales.where('business_id').equals(user.business_id).delete();
            await db.customers.where('business_id').equals(user.business_id).delete();
          }
          if (user?.id) {
            await db.profiles.delete(user.id);
          }

          // 3. Cerrar sesión
          localStorage.removeItem('niiz-auth-storage');
          set({ user: null, isAuthenticated: false, isLocked: false, isLoading: false });

          return { success: true };
        } catch (e: any) {
          return { success: false, error: e?.message || 'Error al eliminar la cuenta' };
        }
      },

      // --- REGISTRO LOCAL OFFLINE ---
      registerLocal: async ({ fullName, businessName, businessType, pin, country, city, address }) => {
        try {
          // 1. Validaciones básicas
          if (!fullName.trim()) return { success: false, error: 'El nombre es obligatorio' };
          if (!businessName.trim()) return { success: false, error: 'El nombre del negocio es obligatorio' };
          if (pin.length !== 4 || !/^\d{4}$/.test(pin)) return { success: false, error: 'El PIN debe ser 4 dígitos numéricos' };

          // 2. Verificar que el PIN no esté en uso
          const existingPin = await db.profiles.where('pin').equals(pin).first();
          if (existingPin) return { success: false, error: 'Ese PIN ya está en uso, elige otro' };

          // 3. Generar IDs
          const newId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const businessId = `biz-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const now = new Date().toISOString();

          // 4. Crear perfil en Dexie
          await db.profiles.add({
            id: newId,
            business_id: businessId,
            full_name: fullName.trim(),
            email: '',
            role: 'ADMIN',
            pin,
            is_active: true,
            created_at: now,
          } as any);

          // 5. Crear configuración del negocio en Dexie
          const firmTrial = businessType === 'ACCOUNTING_FIRM';
          await db.settings.add({
            id: `settings-${businessId}`,
            business_id: businessId,
            business_name: businessName.trim(),
            business_type: businessType,
            currency_symbol: '$',
            tax_rate: 0,
            // 📍 Ubicación obligatoria del negocio.
            country: country?.trim() || '',
            city: city?.trim() || '',
            address: address?.trim() || '',
            plan_type: firmTrial ? 'STUDIO_FIRM' : 'FREE',
            subscription_status: 'TRIAL',
            plan_started_at: now,
            auto_renew: false,
            ...(firmTrial
              ? {
                  subscription_expiry: new Date(Date.now() + STUDIO_TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
                  plan_expires_at: new Date(Date.now() + STUDIO_TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString(),
                }
              : {}),
            beta_type: 'NONE',
            ai_enabled: true,
            marketplace_enabled: false,
            created_at: now,
          } as any);

          // 6. Construir objeto User para la sesión
          const newUser: User = {
            id: newId,
            full_name: fullName.trim(),
            role: 'ADMIN',
            business_id: businessId,
            business_type: businessType,
            plan_type: firmTrial ? 'STUDIO_FIRM' : 'FREE',
            is_active: true,
          };

          return { success: true, user: newUser };
        } catch (e: any) {
          console.error('Error en registerLocal:', e);
          return { success: false, error: e?.message || 'Error al crear la cuenta local' };
        }
      },


      hasPermission: (allowedRoles) => {
        const { user, isAuthenticated } = get();
        if (!isAuthenticated || !user) return false;
        if ((SUPER_ROLES as readonly string[]).includes(user.role)) return true;
        const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
        return roles.includes(user.role);
      },

      can: (action) => {
        const { user } = get();
        if (!user) return false;
        if ((SUPER_ROLES as readonly string[]).includes(user.role)) return true;
        return user.permissions?.includes(action) ?? false;
      },

      getInitials: () => {
        const { user } = get();
        if (!user?.full_name) return '??';
        const parts = user.full_name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      },

      getRoleName: () => {
        const { user } = get();
        if (!user) return 'Invitado';
        const roleMap: Record<string, string> = {
          ADMIN: 'Administrador',
          MANAGER: 'Gerente',
          CASHIER: 'Cajero',
          WAITER: 'Mesero',
          KITCHEN: 'Cocina',
          RECEPTION: 'Recepción',
          HOUSEKEEPING: 'Limpieza',
          SUPPLIER: 'Proveedor B2B'
        };
        return roleMap[user.role] || user.role;
      },
    }),
    {
      name: 'niiz-auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isLoading = false;
          // AUTO-REFRESH: Al recargar la página, actualizamos los datos desde Dexie
          state.refreshSession();
        }
      },
      partialize: (state) => {
        if (!state.user) return { user: null, isAuthenticated: false, isLocked: false };
        const { pin, ...safeUser } = state.user;
        return { user: safeUser, isAuthenticated: state.isAuthenticated, isLocked: state.isLocked };
      },
    }
  )
);